import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IndianRupee, Lock, Phone } from 'lucide-react';
import { EmptyState } from '../components/feedback/empty-state.js';
import { LoadError } from '../components/feedback/load-error.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge, Button, Input, ListSkeleton, cn } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useDayBook, useOutstanding } from '../lib/payments/use-payments.js';
import { formatCurrency, formatDate, formatMobile, formatTime } from '../lib/format.js';

/**
 * The two money questions a shop asks every day, on one screen with two tabs.
 *
 * "What came in today?" is asked once, at closing, with the drawer open — so the day book
 * leads with the three totals that have to match what is being counted, and lists the entries
 * underneath in the order they came in.
 *
 * "Who still owes me?" is asked on a slow morning, with a phone in hand — so the outstanding
 * list leads with the oldest debt, puts the customer's number on the row as a tap-to-call, and
 * links straight to the order in case the answer is "it is not finished yet".
 *
 * It is not in the bottom navigation, which has five slots and no sixth: this is reached from
 * the order book, where the shop already is when it thinks about money.
 */

/** Today in the shop's timezone, as `<input type="date">` wants it. */
function todayInputValue(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

type Tab = 'day_book' | 'outstanding';

export function PaymentsPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [tab, setTab] = useState<Tab>('day_book');
  const [date, setDate] = useState(todayInputValue);

  if (role === 'tailor') {
    return (
      <>
        <PageHeader title={t('payments.title')} />
        <EmptyState
          icon={Lock}
          title={t('payments.noAccessTitle')}
          description={t('payments.noAccessHint')}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('payments.title')} description={t('payments.description')} />

      <div className="flex flex-col gap-4">
        <div
          role="tablist"
          aria-label={t('payments.title')}
          className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1"
        >
          {(['day_book', 'outstanding'] as const).map((one) => (
            <button
              key={one}
              type="button"
              role="tab"
              aria-selected={tab === one}
              onClick={() => setTab(one)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                tab === one
                  ? 'border-transparent bg-primary text-primary-foreground'
                  : 'border-border hover:bg-accent',
              )}
            >
              {t(`payments.tab.${one}`)}
            </button>
          ))}
        </div>

        {tab === 'day_book' ? (
          <DayBookPanel date={date} onDateChange={setDate} />
        ) : (
          <OutstandingPanel />
        )}
      </div>
    </>
  );
}

function DayBookPanel({
  date,
  onDateChange,
}: {
  date: string;
  onDateChange: (value: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const dayBook = useDayBook(date);
  const data = dayBook.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium" htmlFor="day-book-date">
          {t('payments.dayBookDate')}
        </label>
        <Input
          id="day-book-date"
          type="date"
          value={date}
          max={todayInputValue()}
          className="max-w-56"
          onChange={(event) => onDateChange(event.target.value)}
        />
      </div>

      {dayBook.isPending ? (
        <ListSkeleton rows={4} />
      ) : dayBook.isError ? (
        <LoadError onRetry={() => void dayBook.refetch()} />
      ) : !data || data.entries.length === 0 ? (
        <EmptyState
          icon={IndianRupee}
          title={t('payments.dayBookEmptyTitle')}
          description={t('payments.dayBookEmptyHint')}
        />
      ) : (
        <>
          {/* The three numbers the drawer is counted against. */}
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-border p-3">
              <dt className="text-xs text-muted-foreground">{t('payments.received')}</dt>
              <dd className="text-lg font-semibold tabular-nums">
                {formatCurrency(data.totals.receivedPaise)}
              </dd>
            </div>
            <div className="rounded-lg border border-border p-3">
              <dt className="text-xs text-muted-foreground">{t('payments.refunded')}</dt>
              <dd className="text-lg font-semibold tabular-nums">
                {formatCurrency(data.totals.refundedPaise)}
              </dd>
            </div>
            <div className="rounded-lg border border-border p-3">
              <dt className="text-xs text-muted-foreground">{t('payments.inDrawer')}</dt>
              <dd className="text-lg font-semibold tabular-nums">
                {formatCurrency(data.totals.cashPaise)}
              </dd>
            </div>
            <div className="rounded-lg border border-border p-3">
              <dt className="text-xs text-muted-foreground">{t('payments.digital')}</dt>
              <dd className="text-lg font-semibold tabular-nums">
                {formatCurrency(data.totals.digitalPaise)}
              </dd>
            </div>
          </dl>

          {data.totals.chequePaise !== 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('payments.chequesHeld', { amount: formatCurrency(data.totals.chequePaise) })}
            </p>
          ) : null}

          <ul className="flex flex-col gap-2">
            {data.entries.map((entry) => (
              <li key={entry.id}>
                {/* An alteration's money opens the alteration; everything else, its order. */}
                <Link
                  to={
                    entry.alterationId
                      ? `/alterations/${entry.alterationId}`
                      : `/orders/${entry.orderId ?? ''}`
                  }
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      {entry.customerName}
                      {entry.kind === 'refund' ? (
                        <Badge variant="secondary">{t('payments.refundBadge')}</Badge>
                      ) : null}
                      {entry.kind === 'reversal' ? (
                        <Badge variant="warning">{t('payments.reversal')}</Badge>
                      ) : null}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {entry.orderNumber ?? entry.alterationNumber} ·{' '}
                      {t(`payments.methods.${entry.method}`)} · {formatTime(entry.receivedAt)}
                      {entry.recordedByName ? ` · ${entry.recordedByName}` : ''}
                    </p>
                  </div>
                  <p className="font-semibold tabular-nums">
                    {entry.signedAmountPaise < 0 ? '−' : ''}
                    {formatCurrency(entry.amountPaise)}
                  </p>
                </Link>
              </li>
            ))}
          </ul>

          <p className="text-sm text-muted-foreground" role="status">
            {t('payments.netForDay', { amount: formatCurrency(data.totals.netPaise) })}
          </p>
        </>
      )}
    </div>
  );
}

function OutstandingPanel(): ReactElement {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const outstanding = useOutstanding({
    sortBy: 'age',
    ...(query.trim() ? { q: query.trim() } : {}),
  });
  const data = outstanding.data;

  return (
    <div className="flex flex-col gap-4">
      <Input
        type="search"
        aria-label={t('payments.searchLabel')}
        value={query}
        placeholder={t('payments.searchPlaceholder')}
        onChange={(event) => setQuery(event.target.value)}
      />

      {outstanding.isPending ? (
        <ListSkeleton rows={4} />
      ) : outstanding.isError ? (
        <LoadError onRetry={() => void outstanding.refetch()} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          icon={IndianRupee}
          title={t('payments.outstandingEmptyTitle')}
          description={t('payments.outstandingEmptyHint')}
        />
      ) : (
        <>
          <p className="rounded-lg border border-border p-3 text-sm" role="status">
            {t('payments.owedInTotal', {
              amount: formatCurrency(data.totalOutstandingPaise),
              count: data.total,
            })}
          </p>

          <ul className="flex flex-col gap-2">
            {data.items.map((row) => (
              <li
                key={row.orderId ?? row.alterationId}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <p className="font-medium">{row.customerName}</p>
                  <p className="text-sm text-muted-foreground">
                    {/* An alteration's balance opens the alteration; an order's, the order. */}
                    <Link
                      to={
                        row.alterationId
                          ? `/alterations/${row.alterationId}`
                          : `/orders/${row.orderId ?? ''}`
                      }
                      className="underline-offset-2 hover:underline"
                    >
                      {row.orderNumber ?? row.alterationNumber}
                    </Link>
                    {' · '}
                    {t('payments.bookedDaysAgo', { count: row.ageDays })}
                    {row.lastPaymentAt
                      ? ` · ${t('payments.lastPaid', { date: formatDate(row.lastPaymentAt) })}`
                      : ` · ${t('payments.neverPaid')}`}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <p className="font-semibold tabular-nums">
                    {formatCurrency(row.outstandingPaise)}
                  </p>
                  {/* Chasing a balance is a phone call, so it is one tap from the row. */}
                  <Button asChild variant="outline" size="sm">
                    <a
                      href={`tel:${row.customerPhone}`}
                      aria-label={t('payments.callCustomer', { name: row.customerName })}
                    >
                      <Phone aria-hidden="true" />
                      {formatMobile(row.customerPhone)}
                    </a>
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default PaymentsPage;
